import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenForm } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import CodeIcon from '@mui/icons-material/Code';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { usePermissions } from '@/hooks/usePermissions';
import { notify } from '@/core/toast/notify';

interface FormField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'date' | 'dropdown' | 'checkbox' | 'radio' | 'multiselect' | 'file';
  required: boolean;
  options?: string[];
}

export function LeadGenFormsPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('LEAD_GEN_MANAGE');
  const [forms, setForms] = useState<LeadGenForm[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<FormField[]>([
    { key: 'name', label: 'Full name', type: 'text', required: true },
    { key: 'email', label: 'Email address', type: 'email', required: true },
  ]);

  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<FormField['type']>('text');
  const [newFieldOptions, setNewFieldOptions] = useState('');

  const [formType, setFormType] = useState('GENERAL');
  const [publishOnCreate, setPublishOnCreate] = useState(true);
  const [activeEmbedTab, setActiveEmbedTab] = useState<'iframe' | 'js' | 'react' | 'vue' | 'wp'>('iframe');
  const [embedCodes, setEmbedCodes] = useState<Record<string, string> | null>(null);
  const [selectedFormForEmbed, setSelectedFormForEmbed] = useState<LeadGenForm | null>(null);

  const load = useCallback(async () => {
    try {
      setForms(await leadgenApi.listForms());
    } catch (err) {
      showApiError(err, 'Failed to load forms');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addField = () => {
    if (!newFieldLabel.trim()) return;
    const key = newFieldLabel.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const options = newFieldType === 'dropdown' || newFieldType === 'radio' || newFieldType === 'multiselect'
      ? newFieldOptions.split(',').map(o => o.trim()).filter(Boolean)
      : undefined;

    setFields([...fields, { key, label: newFieldLabel, type: newFieldType, required: false, options }]);
    setNewFieldLabel('');
    setNewFieldOptions('');
  };

  const removeField = (key: string) => {
    setFields(fields.filter(f => f.key !== key));
  };

  const create = async () => {
    if (!name.trim()) return;
    try {
      await leadgenApi.createForm({
        name,
        description,
        formType,
        fieldsJson: JSON.stringify(fields),
        published: publishOnCreate,
        active: true
      });
      notify.success('Form created and published successfully!');
      setName('');
      setDescription('');
      setFields([
        { key: 'name', label: 'Full name', type: 'text', required: true },
        { key: 'email', label: 'Email address', type: 'email', required: true },
      ]);
      load();
    } catch (err) {
      showApiError(err, 'Failed to create form');
    }
  };

  const loadEmbed = async (form: LeadGenForm) => {
    setSelectedFormForEmbed(form);
    try {
      const res = await leadgenApi.getFormEmbed(form.id);
      setEmbedCodes(res.embedCodes);
    } catch {
      setEmbedCodes(generateEmbed(form));
    }
  };

  const togglePublish = async (form: LeadGenForm) => {
    try {
      await leadgenApi.updateForm(form.id, { published: !form.published, active: form.active, name: form.name });
      notify.success(form.published ? 'Form unpublished' : 'Form published');
      load();
    } catch (err) {
      showApiError(err, 'Failed to update form');
    }
  };

  const removeForm = async (form: LeadGenForm) => {
    try {
      await leadgenApi.deleteForm(form.id);
      notify.success('Form archived');
      load();
    } catch (err) {
      showApiError(err, 'Failed to delete form');
    }
  };

  const generateEmbed = (form: LeadGenForm): Record<string, string> => {
    const origin = window.location.origin;
    return {
      iframe: `<iframe src="${origin}/embed/lead/${form.publicKey}" width="100%" height="600" frameborder="0"></iframe>`,
      js: `<script src="${origin}/cdn/leadgen.js" data-form-id="${form.publicKey}"></script>`,
      react: `import { LeadGenForm } from '@saaspos/leadgen-react';\n\n<LeadGenForm publicKey="${form.publicKey}" />`,
      vue: `<LeadGenForm publicKey="${form.publicKey}" />`,
      wp: `[leadgen_form id="${form.publicKey}"]`
    };
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Acquisition Lead Forms"
        description="Visual Form Builder and secure embedding configurations."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Forms' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Visual Form Editor */}
        <div className="lg:col-span-2 card p-5 space-y-6">
          {canManage ? (
            <>
          <h3 className="section-title flex items-center gap-2">
            <PostAddIcon /> Create Acquisition Form
          </h3>

          <div className="space-y-4">
            <Input label="Form Name" placeholder="e.g. Inbound Demo Request" value={name} onChange={(e) => setName(e.target.value)} />
            <Input label="Description" placeholder="Optional internal description..." value={description} onChange={(e) => setDescription(e.target.value)} />
            <Select
              label="Capture Type"
              value={formType}
              onChange={(v) => setFormType(v)}
              options={[
                { value: 'GENERAL', label: 'General' },
                { value: 'CONTACT_US', label: 'Contact Us' },
                { value: 'DEMO_REQUEST', label: 'Demo Request' },
                { value: 'QUOTE_REQUEST', label: 'Quote Request' },
                { value: 'SUPPORT_REQUEST', label: 'Support Request' },
                { value: 'NEWSLETTER_SIGNUP', label: 'Newsletter Signup' },
              ]}
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={publishOnCreate} onChange={(e) => setPublishOnCreate(e.target.checked)} />
              Publish immediately
            </label>
          </div>

          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50 space-y-4">
            <h4 className="font-semibold text-slate-700 text-sm">Visual Fields Simulation</h4>
            <div className="space-y-3">
              {fields.map((f, i) => (
                <div key={i} className="flex justify-between items-center bg-white p-3 rounded-lg border border-slate-200 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                  <div>
                    <span className="font-semibold text-slate-800">{f.label}</span>
                    <span className="text-xs text-slate-400 ml-2 bg-slate-100 px-2 py-0.5 rounded-full">{f.type}</span>
                  </div>
                  <button className="text-xs text-red-500 hover:underline" onClick={() => removeField(f.key)}>Remove</button>
                </div>
              ))}
            </div>

            {/* Field Adder form */}
            <div className="border-t border-slate-200 pt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="Field Label" placeholder="e.g. Organization Name" value={newFieldLabel} onChange={(e) => setNewFieldLabel(e.target.value)} />
              <Select
                label="Field Type"
                value={newFieldType}
                onChange={(v) => setNewFieldType(v as FormField['type'])}
                options={[
                  { value: 'text', label: 'Text Input' },
                  { value: 'email', label: 'Email Address' },
                  { value: 'phone', label: 'Phone / Mobile' },
                  { value: 'date', label: 'Date picker' },
                  { value: 'dropdown', label: 'Dropdown Select' },
                  { value: 'checkbox', label: 'Single Checkbox' },
                  { value: 'radio', label: 'Radio Buttons' },
                  { value: 'multiselect', label: 'Multi Select' },
                  { value: 'file', label: 'File Upload' },
                ]}
              />
              <div className="flex items-end">
                <Button variant="secondary" className="w-full" onClick={addField}>Add Field</Button>
              </div>
            </div>

            {(newFieldType === 'dropdown' || newFieldType === 'radio' || newFieldType === 'multiselect') && (
              <Input label="Options (Comma separated)" placeholder="e.g. Small, Medium, Enterprise" value={newFieldOptions} onChange={(e) => setNewFieldOptions(e.target.value)} />
            )}
          </div>

          <Button className="w-full" onClick={create}>Save & Publish Form</Button>
            </>
          ) : (
            <p className="text-sm text-slate-500">You have view-only access. Contact an admin to create forms.</p>
          )}
        </div>

        {/* Existing forms & embeds */}
        <div className="card p-5 space-y-6">
          <h3 className="section-title flex items-center gap-2">
            <CodeIcon /> Active Forms List
          </h3>

          <ul className="space-y-4">
            {forms.map((form) => (
              <li key={form.id} className="border border-slate-100 rounded-lg p-4 shadow-sm bg-slate-50 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-slate-800">{form.name}</p>
                    <p className="text-xs text-slate-400">UUID: {form.publicKey}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${form.published ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {form.published ? 'Published' : 'Draft'}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => loadEmbed(form)}>Get Embed Code</Button>
                  {canManage && (
                    <>
                      <Button variant="secondary" onClick={() => togglePublish(form)}>{form.published ? 'Unpublish' : 'Publish'}</Button>
                      <Button variant="secondary" onClick={() => removeForm(form)}>Archive</Button>
                    </>
                  )}
                  <a className="text-xs text-primary-600 font-semibold hover:underline flex items-center" href={`/embed/lead/${form.publicKey}`} target="_blank" rel="noreferrer">
                    Test Public View
                  </a>
                </div>
              </li>
            ))}
          </ul>

          {/* Embed panel modal popup overlay simulation */}
          {selectedFormForEmbed && (
            <div className="border border-slate-200 rounded-lg p-4 bg-white space-y-3 shadow-inner dark:border-slate-700 dark:bg-slate-900">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-sm text-slate-800">Embed Configurations</h4>
                <button className="text-xs text-slate-400 hover:text-slate-600" onClick={() => { setSelectedFormForEmbed(null); setEmbedCodes(null); }}>Close</button>
              </div>

              <div className="flex gap-2 border-b border-slate-100 pb-2">
                {(['iframe', 'js', 'react', 'vue', 'wp'] as const).map(tab => (
                  <button key={tab} className={`text-xs px-2 py-1 rounded ${activeEmbedTab === tab ? 'bg-primary-600 text-white font-semibold' : 'text-slate-500 hover:bg-slate-100'}`} onClick={() => setActiveEmbedTab(tab)}>
                    {tab.toUpperCase()}
                  </button>
                ))}
              </div>

              <pre className="text-[10px] bg-slate-900 text-emerald-400 p-3 rounded font-mono overflow-x-auto whitespace-pre-wrap select-all">
                {(embedCodes ?? generateEmbed(selectedFormForEmbed))[activeEmbedTab]}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
